import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../../stores';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';
import SpiteToggle from '../SpiteToggle';
import ImageUpload from '../ImageUpload';

// Модалка челлендж-шаблона (библиотека). Категория/подкатегория фиксированы (дефолтный «дом челленджей» пака).
// Механика эстафеты — только релевантные поля, без покупки/дуэлей/рулетки.
export default function ChallengeTemplateModal({ template, categoryId, subcategoryId, onSaved, onClose }) {
    const editing = !!template;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [file, setFile] = useState(null);
    const [clearImage, setClearImage] = useState(false);
    const [form, setForm] = useState({
        title: template?.title || '',
        description: template?.description || '',
        conditions: template?.conditions || '',
        cost: template?.cost ?? 50,
        duration: template?.duration || '',
        isActive: editing ? template.isActive !== false : true
    });
    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const save = async () => {
        if (!form.title.trim()) { enqueueSnackbar('Введите название', { variant: 'warning' }); return; }
        const requestData = {
            categoryId,
            subcategoryId,
            title: form.title.trim(),
            description: form.description,
            conditions: form.conditions,
            cost: form.cost,
            isActive: form.isActive,
            isChallenge: true,
            useTime: true,
            duration: form.duration || null,
            withTargetUser: false,
            requiresBothConfirmation: false,
            involvedInDaily: false,
            availableCountType: 'UNLIMITED',
            availableCount: null
        };
        const fd = new FormData();
        fd.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));
        if (file) fd.append('file', file);
        show();
        try {
            if (editing) {
                await apiClient.patch(`/api/neighbor/admin/task-templates/update?id=${template.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await apiClient.post('/api/neighbor/admin/task-templates/create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            enqueueSnackbar(editing ? 'Челлендж-шаблон обновлён' : 'Челлендж-шаблон создан', { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    const del = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/task-templates/delete?id=${template.id}`);
            enqueueSnackbar('Челлендж-шаблон удалён', { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) { enqueueSnackbar('Ошибка при удалении', { variant: 'error' }); }
        finally { hide(); }
    };

    const footer = (
        <>
            {editing && <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={del}>Удалить</button>}
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={save}>{editing ? 'Сохранить' : 'Создать'}</button>
        </>
    );

    return (
        <Modal open kicker="Челлендж-шаблон" title={editing ? 'Редактирование челленджа' : 'Создать челлендж'} size="lg" onClose={onClose} footer={footer}>
            <LabeledInput label="Название" value={form.title} onChange={(e) => set('title', e.target.value)} />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
                <label className="ctl-label">Условия выполнения</label>
                <textarea className="textarea" value={form.conditions} onChange={(e) => set('conditions', e.target.value)} />
            </div>
            <div className="fr-grid2">
                <LabeledInput label="Награда (SOL)" type="number" value={String(form.cost)} onChange={(e) => set('cost', parseInt(e.target.value, 10) || 0)} />
                <LabeledInput label="Лимит времени звена (ЧЧ:ММ:СС)" value={form.duration} onChange={(e) => set('duration', e.target.value)} placeholder="02:00:00" />
            </div>
            <div className="sp-sec">
                <div className="sp-tgs">
                    <SpiteToggle label="Активен" checked={form.isActive} onChange={() => set('isActive', !form.isActive)} full />
                </div>
            </div>
            <div className="sp-sec">
                <h4>Изображение</h4>
                <ImageUpload
                    existingUrl={clearImage ? null : (template?.mediaFile?.mediaFileUrl || null)}
                    pendingFile={file}
                    onFileSelect={(f) => { setFile(f); setClearImage(false); }}
                    onClear={() => { setFile(null); setClearImage(true); }}
                />
            </div>
        </Modal>
    );
}

ChallengeTemplateModal.propTypes = {
    template: PropTypes.object,
    categoryId: PropTypes.number,
    subcategoryId: PropTypes.number,
    onSaved: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

ChallengeTemplateModal.defaultProps = { template: null, categoryId: null, subcategoryId: null };
